import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePlanningData } from '../hooks/usePlanningData';
import { useDraftChanges } from '../hooks/useDraftChanges';
import { useMultiSelect } from '../hooks/useMultiSelect';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { PlanningToolbar } from '../components/planning/PlanningToolbar';
import { PlanListView } from '../components/planning/PlanListView';
import { PlanGanttView } from '../components/planning/PlanGanttView';
import { CreateIssueModal } from '../components/planning/CreateIssueModal';
import { SyncProgressModal } from '../components/planning/SyncProgressModal';
import { ReviewChangesDrawer } from '../components/planning/ReviewChangesDrawer';
import { BulkActionToolbar } from '../components/planning/BulkActionToolbar';
import { ConflictModal } from '../components/planning/ConflictModal';
import { IssueDetailDrawer } from '../components/planning/IssueDetailDrawer';
import { LogWorkModal } from '../components/LogWorkModal';
export function ProjectPlanningPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const { showToast } = useToast();
    const [viewMode, setViewMode] = useState('list');
    const [filters, setFilters] = useState({});
    const [expandedKeys, setExpandedKeys] = useState(new Set());
    const [createModal, setCreateModal] = useState(null);
    const [showReviewDrawer, setShowReviewDrawer] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [approving, setApproving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [conflicts, setConflicts] = useState(null);
    const [detailIssue, setDetailIssue] = useState(null);
    const [logWorkIssue, setLogWorkIssue] = useState(null);
    // Fetch project info
    const { data: projectData } = useApi(`/api/projects/${id}`);
    const project = projectData?.project;
    // Fetch plan tree
    const { tree, loading, error, syncing, syncProgress, showSyncModal, refetch, startSync, closeSyncModal, reorder, moveToEpic, bulkMoveToEpic, updateField, createIssue, } = usePlanningData(id, filters);
    // Draft changes
    const { draftCount, pendingDraftCount, submittedDraftCount, draftGroups, hasDraft, getDraftStatus, addDraft, bulkAddDrafts, removeDraft, discardAllDrafts, submitDrafts, } = useDraftChanges(id, tree, refetch);
    // Compute flat visible keys for multi-select
    const flatVisibleKeys = useMemo(() => {
        if (!tree)
            return [];
        const keys = [];
        const collect = (issues) => {
            for (const issue of issues) {
                keys.push(issue.issueKey);
                if (expandedKeys.has(issue.issueKey)) {
                    collect(issue.children);
                }
            }
        };
        collect(tree.epics);
        collect(tree.unparentedIssues);
        return keys;
    }, [tree, expandedKeys]);
    // Visual key order reported by PlanListView (sorted + filtered)
    const [visualKeyOrder, setVisualKeyOrder] = useState([]);
    const handleVisualOrderChange = useCallback((keys) => {
        setVisualKeyOrder(prev => {
            // Avoid re-render if the order hasn't actually changed
            if (prev.length === keys.length && prev.every((k, i) => k === keys[i])) {
                return prev;
            }
            return keys;
        });
    }, []);
    // Use PlanListView's visual order when available, fall back to tree order
    const effectiveKeyOrder = visualKeyOrder.length > 0 ? visualKeyOrder : flatVisibleKeys;
    // Multi-select
    const { selectedKeys, toggle: toggleSelect, selectAll, clearSelection, selectionCount, } = useMultiSelect(effectiveKeyOrder);
    const canEdit = user?.role === 'portfolio_manager' || user?.role === 'project_manager';
    const jiraBaseUrl = projectData?.jiraInstanceUrl || undefined;
    // Use versions, assignees, epics, statuses, and sprints from the API response
    const versions = tree?.versions ?? [];
    const assignees = tree?.assignees ?? [];
    const epicOptions = tree?.epicOptions ?? [];
    const statuses = tree?.statuses ?? [];
    const sprints = tree?.sprints ?? [];
    // Warn on browser close/reload with unsaved pending drafts
    useEffect(() => {
        const handler = (e) => {
            if (pendingDraftCount > 0) {
                e.preventDefault();
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [pendingDraftCount]);
    const handleToggleExpand = useCallback((key) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            return next;
        });
    }, []);
    const handleExpandAll = useCallback(() => {
        if (!tree)
            return;
        const keys = new Set();
        const traverse = (issues) => {
            for (const issue of issues) {
                if (issue.children.length > 0) {
                    keys.add(issue.issueKey);
                    traverse(issue.children);
                }
            }
        };
        traverse(tree.epics);
        traverse(tree.unparentedIssues);
        setExpandedKeys(keys);
    }, [tree]);
    const handleCollapseAll = useCallback(() => {
        setExpandedKeys(new Set());
    }, []);
    const handleAddChild = useCallback((parentKey, issueType) => {
        const epicKey = issueType === 'Story' ? parentKey : undefined;
        setCreateModal({
            parentKey: issueType === 'Sub-task' ? parentKey : undefined,
            epicKey,
            issueType,
        });
    }, []);
    const handleAddEpic = useCallback(() => {
        setCreateModal({ issueType: 'Epic' });
    }, []);
    const handleSync = useCallback(async () => {
        try {
            await startSync();
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to sync from Jira', 'error');
        }
    }, [startSync, showToast]);
    const handleCreateSubmit = useCallback(async (issueType, summary, parentKey, epicKey, additionalFields) => {
        try {
            await createIssue(issueType, summary, parentKey, epicKey, additionalFields);
            showToast('Issue creation submitted for approval', 'info');
            setCreateModal(null);
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to create issue', 'error');
        }
    }, [createIssue, showToast]);
    // Draft-aware field save: creates a draft instead of immediate write-back
    const handleDraftFieldSave = useCallback(async (issueKey, fieldName, oldValue, newValue) => {
        await addDraft(issueKey, fieldName, oldValue, newValue);
        showToast('Draft saved', 'info');
        return ''; // No requestId for drafts
    }, [addDraft, showToast]);
    // Submit flow: check conflicts first, then submit
    const handleSubmitDrafts = useCallback(async () => {
        setSubmitting(true);
        try {
            const result = await submitDrafts(false);
            if (!result.success && result.conflicts && result.conflicts.length > 0) {
                setConflicts(result.conflicts);
                setShowReviewDrawer(false);
            }
            else {
                showToast(`${result.requestIds.length} change(s) submitted for approval`, 'success');
                setShowReviewDrawer(false);
            }
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to submit changes', 'error');
        }
        finally {
            setSubmitting(false);
        }
    }, [submitDrafts, showToast]);
    // Force submit (override conflicts)
    const handleForceSubmit = useCallback(async () => {
        setSubmitting(true);
        try {
            const result = await submitDrafts(true);
            if (result.success) {
                showToast(`${result.requestIds.length} change(s) submitted for approval`, 'success');
                setConflicts(null);
            }
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to submit changes', 'error');
        }
        finally {
            setSubmitting(false);
        }
    }, [submitDrafts, showToast]);
    // Approve & Execute all submitted changes, then refetch
    const handleApproveSubmitted = useCallback(async () => {
        if (!id)
            return;
        setApproving(true);
        try {
            const response = await fetch(`/api/planning/${id}/approve-submitted`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || 'Failed to approve changes');
            }
            const data = await response.json();
            if (data.failed > 0) {
                showToast(`${data.approved} approved, ${data.failed} failed: ${data.errors?.map((e) => e.error).slice(0, 2).join('; ')}`, 'error');
            }
            else {
                showToast(`${data.approved} change(s) approved and executed`, 'success');
            }
            // Auto-refetch to show updated data
            await refetch();
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to approve changes', 'error');
        }
        finally {
            setApproving(false);
        }
    }, [id, showToast, refetch]);
    // Export project plan
    const handleExport = useCallback(async (format) => {
        if (!id)
            return;
        setExporting(true);
        try {
            const params = new URLSearchParams();
            params.set('format', format);
            if (filters.versions?.length)
                params.set('versions', filters.versions.join(','));
            if (filters.epics?.length)
                params.set('epics', filters.epics.join(','));
            if (filters.assignees?.length)
                params.set('assignees', filters.assignees.join(','));
            if (filters.statuses?.length)
                params.set('statuses', filters.statuses.join(','));
            if (filters.sprints?.length)
                params.set('sprints', filters.sprints.join(','));
            if (filters.search)
                params.set('search', filters.search);
            const response = await fetch(`/api/planning/${id}/export?${params.toString()}`, {
                credentials: 'include',
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || 'Export failed');
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            // Extract filename from Content-Disposition header or use default
            const disposition = response.headers.get('Content-Disposition');
            a.download = disposition?.match(/filename="(.+)"/)?.[1]
                ?? `project-plan.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            showToast('Export downloaded successfully', 'success');
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to export', 'error');
        }
        finally {
            setExporting(false);
        }
    }, [id, filters, showToast]);
    // Revert a single draft
    const handleRevertDraft = useCallback(async (changeId) => {
        try {
            await removeDraft(changeId);
            showToast('Draft reverted', 'info');
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to revert draft', 'error');
        }
    }, [removeDraft, showToast]);
    // Discard all drafts
    const handleDiscardAll = useCallback(async () => {
        try {
            await discardAllDrafts();
            showToast('All drafts discarded', 'info');
            setShowReviewDrawer(false);
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to discard drafts', 'error');
        }
    }, [discardAllDrafts, showToast]);
    // Find issue by key in the tree (for detail drawer)
    const findIssueByKey = useCallback((key) => {
        if (!tree)
            return null;
        const search = (issues) => {
            for (const issue of issues) {
                if (issue.issueKey === key)
                    return issue;
                const found = search(issue.children);
                if (found)
                    return found;
            }
            return null;
        };
        return search(tree.epics) || search(tree.unparentedIssues);
    }, [tree]);
    const handleViewDetails = useCallback((issueKey) => {
        const issue = findIssueByKey(issueKey);
        if (issue)
            setDetailIssue(issue);
    }, [findIssueByKey]);
    // Keep detailIssue in sync with tree data (e.g. after draft overlay)
    useEffect(() => {
        if (detailIssue && tree) {
            const updated = findIssueByKey(detailIssue.issueKey);
            if (updated && updated !== detailIssue) {
                setDetailIssue(updated);
            }
        }
    }, [tree, detailIssue, findIssueByKey]);
    // Bulk operations
    const handleBulkSetAssignee = useCallback((accountId) => {
        const drafts = Array.from(selectedKeys).map(issueKey => ({
            issueKey,
            fieldName: 'assignee',
            newValue: accountId,
        }));
        void bulkAddDrafts(drafts).then(() => {
            showToast(`Assignee draft set for ${drafts.length} issue(s)`, 'info');
            clearSelection();
        }).catch(err => {
            showToast(err instanceof Error ? err.message : 'Failed to set assignee', 'error');
        });
    }, [selectedKeys, bulkAddDrafts, showToast, clearSelection]);
    const handleBulkSetSP = useCallback((points) => {
        const drafts = Array.from(selectedKeys).map(issueKey => ({
            issueKey,
            fieldName: 'story_points',
            newValue: String(points),
        }));
        void bulkAddDrafts(drafts).then(() => {
            showToast(`Story points draft set for ${drafts.length} issue(s)`, 'info');
            clearSelection();
        }).catch(err => {
            showToast(err instanceof Error ? err.message : 'Failed to set story points', 'error');
        });
    }, [selectedKeys, bulkAddDrafts, showToast, clearSelection]);
    const handleBulkSetDueDate = useCallback((date) => {
        const drafts = Array.from(selectedKeys).map(issueKey => ({
            issueKey,
            fieldName: 'due_date',
            newValue: date,
        }));
        void bulkAddDrafts(drafts).then(() => {
            showToast(`Due date draft set for ${drafts.length} issue(s)`, 'info');
            clearSelection();
        }).catch(err => {
            showToast(err instanceof Error ? err.message : 'Failed to set due date', 'error');
        });
    }, [selectedKeys, bulkAddDrafts, showToast, clearSelection]);
    const handleBulkSetStartDate = useCallback((date) => {
        const drafts = Array.from(selectedKeys).map(issueKey => ({
            issueKey,
            fieldName: 'start_date',
            newValue: date,
        }));
        void bulkAddDrafts(drafts).then(() => {
            showToast(`Start date draft set for ${drafts.length} issue(s)`, 'info');
            clearSelection();
        }).catch(err => {
            showToast(err instanceof Error ? err.message : 'Failed to set start date', 'error');
        });
    }, [selectedKeys, bulkAddDrafts, showToast, clearSelection]);
    const handleBulkSetOriginalEstimate = useCallback((seconds) => {
        const drafts = Array.from(selectedKeys).map(issueKey => ({
            issueKey,
            fieldName: 'original_estimate',
            newValue: seconds,
        }));
        void bulkAddDrafts(drafts).then(() => {
            showToast(`Original estimate draft set for ${drafts.length} issue(s)`, 'info');
            clearSelection();
        }).catch(err => {
            showToast(err instanceof Error ? err.message : 'Failed to set original estimate', 'error');
        });
    }, [selectedKeys, bulkAddDrafts, showToast, clearSelection]);
    const handleBulkSetStatus = useCallback((status) => {
        const drafts = Array.from(selectedKeys).map(issueKey => ({
            issueKey,
            fieldName: 'status',
            newValue: status,
        }));
        void bulkAddDrafts(drafts).then(() => {
            showToast(`Status draft set for ${drafts.length} issue(s)`, 'info');
            clearSelection();
        }).catch(err => {
            showToast(err instanceof Error ? err.message : 'Failed to set status', 'error');
        });
    }, [selectedKeys, bulkAddDrafts, showToast, clearSelection]);
    if (error) {
        return (_jsx("div", { className: "space-y-4", children: _jsxs("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 text-red-700", children: [_jsx("p", { className: "font-medium", children: "Failed to load planning data" }), _jsx("p", { className: "text-sm mt-1", children: error }), _jsx("button", { onClick: refetch, className: "text-sm underline mt-2", children: "Retry" })] }) }));
    }
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-center gap-2 text-sm text-text-secondary", children: [_jsx(Link, { to: "/portfolio", className: "hover:text-primary-600", children: "Portfolio" }), _jsx("span", { children: "/" }), _jsx(Link, { to: `/projects/${id}`, className: "hover:text-primary-600", children: project?.name || 'Project' }), _jsx("span", { children: "/" }), _jsx("span", { className: "text-text-primary font-medium", children: "Planning" })] }), tree && (_jsx("div", { className: "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3", children: [
                    { label: 'Epics', value: tree.summary.totalEpics, color: 'text-purple-600' },
                    { label: 'Stories', value: tree.summary.totalStories, color: 'text-green-600' },
                    { label: 'Sub-tasks', value: tree.summary.totalSubtasks, color: 'text-blue-600' },
                    { label: 'Story Points', value: tree.summary.totalSP, color: 'text-indigo-600' },
                    { label: 'Complete', value: `${tree.summary.completedPct}%`, color: tree.summary.completedPct >= 80 ? 'text-green-600' : 'text-text-primary' },
                    { label: 'Unassigned', value: tree.summary.unassigned, color: tree.summary.unassigned > 0 ? 'text-amber-600' : 'text-text-primary' },
                    { label: 'Unestimated', value: tree.summary.unestimated, color: tree.summary.unestimated > 0 ? 'text-amber-600' : 'text-text-primary' },
                    { label: 'Overdue', value: tree.summary.overdue, color: tree.summary.overdue > 0 ? 'text-red-600' : 'text-text-primary' },
                ].map(kpi => (_jsxs("div", { className: "bg-white rounded-lg border border-border px-3 py-2", children: [_jsx("div", { className: "text-[10px] text-text-tertiary uppercase tracking-wider", children: kpi.label }), _jsx("div", { className: `text-lg font-semibold ${kpi.color}`, children: kpi.value })] }, kpi.label))) })), _jsx(PlanningToolbar, { filters: filters, onFiltersChange: setFilters, viewMode: viewMode, onViewModeChange: setViewMode, onAddEpic: handleAddEpic, onExpandAll: handleExpandAll, onCollapseAll: handleCollapseAll, onSync: handleSync, syncing: syncing, versions: versions, assignees: assignees, epicOptions: epicOptions, statuses: statuses, sprints: sprints, canEdit: canEdit, draftCount: draftCount, pendingDraftCount: pendingDraftCount, submittedDraftCount: submittedDraftCount, onOpenReviewPanel: () => setShowReviewDrawer(true), onApproveSubmitted: user?.role === 'portfolio_manager' ? handleApproveSubmitted : undefined, approving: approving, onExport: handleExport, exporting: exporting }), loading && !tree ? (_jsxs("div", { className: "bg-white rounded-lg border border-border p-12 text-center", children: [_jsx("div", { className: "animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" }), _jsx("p", { className: "text-text-secondary mt-3 text-sm", children: "Loading planning data..." })] })) : tree ? (viewMode === 'list' ? (_jsx(PlanListView, { epics: tree.epics, unparentedIssues: tree.unparentedIssues, expandedKeys: expandedKeys, onToggleExpand: handleToggleExpand, onReorder: reorder, onMoveToEpic: moveToEpic, onBulkMoveToEpic: bulkMoveToEpic, onUpdateField: canEdit ? handleDraftFieldSave : updateField, onAddChild: handleAddChild, canEdit: canEdit, jiraBaseUrl: jiraBaseUrl, hasDraft: hasDraft, getDraftStatus: getDraftStatus, selectedKeys: selectedKeys, onToggleSelect: canEdit ? toggleSelect : undefined, onSelectAll: canEdit ? selectAll : undefined, onClearSelection: clearSelection, onVisualOrderChange: handleVisualOrderChange, statuses: statuses.map(s => ({ value: s.value, label: s.label })), assignees: assignees, onViewDetails: handleViewDetails })) : (_jsx(PlanGanttView, { epics: tree.epics, unparentedIssues: tree.unparentedIssues, expandedKeys: expandedKeys, onToggleExpand: handleToggleExpand }))) : null, createModal && (_jsx(CreateIssueModal, { issueType: createModal.issueType, parentKey: createModal.parentKey, epicKey: createModal.epicKey, onSubmit: handleCreateSubmit, onClose: () => setCreateModal(null), assignees: assignees })), showSyncModal && syncProgress && (_jsx(SyncProgressModal, { progress: syncProgress, onClose: closeSyncModal })), showReviewDrawer && (_jsx(ReviewChangesDrawer, { draftGroups: draftGroups, draftCount: draftCount, pendingDraftCount: pendingDraftCount, submittedDraftCount: submittedDraftCount, onClose: () => setShowReviewDrawer(false), onRevert: handleRevertDraft, onDiscardAll: handleDiscardAll, onSubmit: handleSubmitDrafts, submitting: submitting })), detailIssue && (_jsx(IssueDetailDrawer, { issue: detailIssue, onClose: () => setDetailIssue(null), onUpdateField: canEdit ? handleDraftFieldSave : updateField, canEdit: canEdit, jiraBaseUrl: jiraBaseUrl, hasDraft: hasDraft, getDraftStatus: getDraftStatus, statuses: statuses.map(s => ({ value: s.value, label: s.label })), assignees: assignees, onLogWork: (issueKey) => {
                    const allIssues = [
                        ...(tree?.epics || []).flatMap(e => [e, ...(e.children || []).flatMap(s => [s, ...(s.children || [])])]),
                        ...(tree?.unparentedIssues || []).flatMap(i => [i, ...(i.children || [])]),
                    ];
                    const found = allIssues.find(i => i.issueKey === issueKey);
                    setLogWorkIssue({ issueKey, summary: found?.summary || issueKey });
                } })), logWorkIssue && (_jsx(LogWorkModal, { issueKey: logWorkIssue.issueKey, issueSummary: logWorkIssue.summary, projectId: id, onClose: () => setLogWorkIssue(null), onSuccess: () => refetch(), canLogOnBehalf: user?.role === 'portfolio_manager' || user?.role === 'project_manager' })), selectionCount > 0 && canEdit && (_jsx(BulkActionToolbar, { selectionCount: selectionCount, assignees: assignees, statuses: statuses.map(s => ({ value: s.value, label: s.label })), onSetAssignee: handleBulkSetAssignee, onSetStoryPoints: handleBulkSetSP, onSetDueDate: handleBulkSetDueDate, onSetStartDate: handleBulkSetStartDate, onSetOriginalEstimate: handleBulkSetOriginalEstimate, onSetStatus: handleBulkSetStatus, onClearSelection: clearSelection })), conflicts && conflicts.length > 0 && (_jsx(ConflictModal, { conflicts: conflicts, onForceSubmit: handleForceSubmit, onCancel: () => setConflicts(null), submitting: submitting }))] }));
}
//# sourceMappingURL=ProjectPlanningPage.js.map